<script lang="ts">
    import Modal from "@/components/Modal.svelte";
    import {Button} from "@/components/ui/button";
    import Input from "@/components/Form/Inputs/Input.svelte";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import FormBlock from "@/components/Form/FormBlock.svelte";
    import type Blocks from "@/schemas/public/Blocks";
    import type { Field } from '@/types/types';
    import {useForm} from "@inertiajs/svelte";
    import {toast} from "svelte-sonner";
    import Checkbox from "@/components/Inputs/Checkbox.svelte";
    import Select from "@/components/Inputs/Select.svelte";
    import Textarea from "@/components/Inputs/Textarea.svelte";
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import { parseFieldOptions } from '@/lib/fieldUtils';
    import Debug from '@/components/Debug.svelte';

    type SiteVisit = {
        id?: string;
        title: string;
        purpose: string | null;
        status: string;
        start_date_time: string;
        end_date_time: string;
        notes: string | null;
        findings: string | null;
        visitable_type: string;
        visitable_id: string;
    };

    type Props = {
        blocks: (Blocks & { fields: Field[] })[];
        show: boolean;
        siteVisit?: SiteVisit | null;
        visitableType: string;
        visitableId: string;
        onSuccess?: (siteVisit: any) => void;
    };

    let {blocks, show = $bindable(), siteVisit = null, visitableType, visitableId, onSuccess}: Props = $props();

    let form = $state(useForm<Record<string, any>>({}));

    // Re-initialize form when modal opens or siteVisit changes
    $effect(() => {
        if (show) {
            const formInner: Record<string, any> = {};
            for (const block of blocks) {
                for (const field of block.fields) {
                    let value = siteVisit?.[field.column_name as keyof SiteVisit] ?? field.value ?? '';

                    // Convert datetime strings to Date objects for datetime fields
                    if (field.ui_type === 'datetime' && typeof value === 'string' && value) {
                        const date = new Date(value);
                        value = !isNaN(date.getTime()) ? date : null;
                    }

                    formInner[field.column_name] = value;
                }
            }
            formInner.visitable_type = visitableType;
            formInner.visitable_id = visitableId;

            if (!siteVisit) {
                formInner.status = 'scheduled';
            }

            form = useForm(formInner);
        }
    });

     const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;

    function handleStartChange(value: Date | null) {
        if (!value) {
            return;
        }
        if (!$form.end_date_time) {
            $form.end_date_time = new Date(value.getTime() + FOUR_HOURS_MS);
            return;
        }
        const endTime = $form.end_date_time instanceof Date
            ? $form.end_date_time
            : new Date($form.end_date_time);
        if (!isNaN(endTime.getTime()) && endTime.getTime() < value.getTime()) {
            $form.end_date_time = new Date(value.getTime() + FOUR_HOURS_MS);
        }
    }

    function submit(e: Event) {
        e.preventDefault();

        if (siteVisit?.id) {
            $form.put(route('site-visits.update', {siteVisit: siteVisit.id}), {
                preserveScroll: true,
                preserveState: true,
                onSuccess: (response) => {
                    const flashData = response.props.flash || {};
                    if (flashData.siteVisit && onSuccess) {
                        onSuccess(flashData.siteVisit);
                    }
                    toast.success(flashData.message || "Site visit updated successfully!");
                    close();
                },
                onError: (errors) => {
                    for (const message of Object.values(errors)) {
                        toast.error("Error updating site visit: " + message as string);
                    }
                }
            });
        } else {
            $form.post(route('site-visits.store'), {
                preserveScroll: true,
                preserveState: true,
                onSuccess: (response) => {
                    const flashData = response.props.flash || {};
                    toast.success(flashData.message || "Site visit created successfully!");
                    close();
                    if (flashData.siteVisit && onSuccess) {
                        onSuccess(flashData.siteVisit);
                    }
                },
                onError: (errors) => {
                    for (const message of Object.values(errors)) {
                        toast.error("Error creating site visit: " + message as string);
                    }
                }
            });
        }
    }

    function close() {
        show = false;
    }
</script>

{#if show}
    <Modal title={siteVisit ? "Edit Site Visit" : "Create Site Visit"} onClose={close}>
        <FormContainer>
            <form onsubmit={submit}>
                {#each blocks as block (block.id)}
                    <FormBlock label={block.label}>
                        {#each block.fields as field (field.id)}
                            {#if field.editable}
                                {#if field.ui_type === 'checkbox'}
                                    <Checkbox
                                        bind:checked={$form[field.column_name]}
                                        label={field.label}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'select'}
                                    <Select
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        options={parseFieldOptions(field)}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'textarea'}
                                    <Textarea
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'datetime'}
                                    <DateTime
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                        minValue={field.column_name === 'end_date_time' ? $form.start_date_time : undefined}
                                        onValueChange={field.column_name === 'start_date_time' ? handleStartChange : undefined}
                                    />
                                {:else}
                                    <Input
                                        field={field}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                        onkeydown={() => {}}
                                    />
                                {/if}
                            {/if}
                        {/each}
                    </FormBlock>
                {/each}

                <div class="mt-5 flex justify-end gap-2">
                    <Button type="button" onclick={close}>Cancel</Button>
                    <Button type="submit" disabled={$form.processing}>
                        {#if $form.processing}Saving...{:else}Save{/if}
                    </Button>
                </div>
            </form>
        </FormContainer>
    </Modal>
{/if}
